import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Can I still claim against Slough after its Section 114 notice?',
    answer: 'Yes. A Section 114 notice does not remove the council\'s duty to maintain highways or your ability to pursue a claim. It can provide context: Slough\'s June 2025 transparency report records that spending was "restricted to emergencies only" after the July 2021 notice. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on the council\'s wider financial position alone.',
  },
  {
    question: 'Slough earns GREEN on DfT best practice — does that block my claim?',
    answer: 'Not automatically. The Department for Transport rates Slough GREEN on wider best practice — the council cites APSE benchmarking, permit-scheme coordination and innovation pilots in its June 2025 report. But the overall rating is RED and the spend scorecard is RED. Your claim lives or dies on inspection records, defect response and evidence for your specific road, not on a single green scorecard.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 268.8km — 86% of Slough\'s 312.2km carriageway network. The council surveys 25% of U-roads annually using CVI methodology (a four-year cycle to cover the network). Its published RED-only figures show U-road failure rates rose from 16% in 2020 to a peak of 25% in 2023, standing at 22% in 2024. If your incident fell between survey years, the council\'s network-level data may not have captured your street.',
  },
  {
    question: 'Does Section 114 guarantee my claim will succeed?',
    answer: 'No. The July 2021 Section 114 notice and the council\'s own admission that maintenance spending was "restricted to emergencies only" may weaken a blanket Section 58 defence — but success still depends on your evidence: photos, repair invoices, road class, prior reports and whether the defect met the council\'s intervention criteria.',
  },
  {
    question: 'Pothole repairs rose from 377 to 1,393 — does the slight fall to 1,393 in 2024/25 mean recovery?',
    answer: 'Not necessarily for your road. Slough filled 377 potholes in 2020/21, 408 in 2021/22 (when Section 114 was issued), then 1,151, 1,461 and 1,393 through 2024/25 — a 269% rise from the pre-notice baseline. The council projects around 1,200 fills in 2025/26. That volume of reactive work shows defects still forming across the network; it is not proof your specific defect was unavoidable or reasonably managed.',
  },
  {
    question: 'Why is Slough\'s DfT spend scorecard RED when it allocates over £1m a year?',
    answer: 'The DfT Spend scorecard compares investment against what the department considers adequate for the network — Slough is one of 13 authorities with an overall RED rating in 2025/26. The council\'s transparency report shows total highway maintenance spend of £1.129m in 2024/25 (£261k capital plus £868k revenue), with capital spend held at £261k since 2022/23 despite rising DfT allocations. Aggregate spend does not prove the individual pothole was known and repaired within inspection intervals.',
  },
  {
    question: 'How do I report a pothole to Slough Borough Council?',
    answer: 'Report highways defects via the council\'s online form at slough.gov.uk. Prior reports of the same defect can demonstrate the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack. If you are claiming compensation for damage, that is a separate insurance claim process on the council\'s website.',
  },
];

const TRANSPARENCY_REPORT_URL =
  'https://www.slough.gov.uk/downloads/file/4777/highway-maintenance-transparency-report-june-2025';
const HIGHWAYS_FUNDING_URL = 'https://www.slough.gov.uk/parking-roads-travel/highways-maintenance-funding';
const REPORT_POTHOLE_URL = 'https://www.slough.gov.uk/xfp/form/152';

export function SloughCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Slough Pothole Claims | 1,393 Repairs on RED-Rated Network | Fixtyer"
        description="Slough Borough Council: DfT RED overall rating, Section 114 spending restrictions from July 2021, and 1,393 pothole repairs in 2024/25 — up 269% from 2020/21. See what this means for your Section 58 claim."
        canonical="/councils/slough"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Slough', url: '/councils/slough' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Slough Borough Council"
        councilSlug="slough"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/slough"
        headline="Slough Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Slough</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-rose-200 text-sm">
              <strong className="text-rose-400">Section 114 context:</strong> Slough issued a Section 114 notice in
              July 2021. Its June 2025 transparency report records that revenue and capital spend was then{' '}
              <strong className="text-white">restricted to emergencies only</strong> — with no condition surveys
              collected in 2022.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            1,393 Pothole Repairs in 2024/25 on a RED-Rated Borough
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Slough Borough Council maintains 312.2km of roads — 268.8km of them unclassified residential streets.
            The Department for Transport rates the borough <strong className="text-rose-400">RED</strong> overall and
            on spend, <strong className="text-amber-400">AMBER</strong> on condition, yet{' '}
            <strong className="text-emerald-400">GREEN</strong> on best practice. After July 2021 Section 114
            restrictions, estimated pothole fills rose from <strong className="text-rose-400">377 in 2020/21</strong> to{' '}
            <strong className="text-rose-400">1,393 in 2024/25</strong> — a 269% increase. Section 58 still turns
            on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">269%</div>
            <div className="text-white font-medium mt-2">Rise in pothole repairs since Section 114</div>
            <div className="text-brand-300 text-sm mt-1">
              From 377 estimated fills in 2020/21 to 1,393 in 2024/25 — against roughly £1.129m total maintenance
              spend in 2024/25, with 18% of reactive spend attributed to pothole intervention works.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport rating: RED
          </h2>
          <p className="text-surface-600 mb-8">
            Official 2025/26 scorecards — one of 13 authorities rated RED overall in England
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Slough Borough Council DfT road maintenance scorecards 2025/26</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Metric</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall rating</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition scorecard</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend scorecard</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best practice scorecard</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 mb-4">
            <h3 className="font-semibold text-rose-900 mb-3">What RED overall means</h3>
            <p className="text-rose-800 mb-3">
              Slough is one of <strong>13 councils in England (out of 154)</strong> to receive an overall RED rating
              from the Department for Transport in 2025/26. The spend scorecard RED reflects inadequate investment
              relative to DfT expectations — not a finding that every road is unmaintained.
            </p>
            <p className="text-rose-800 text-sm">
              The GREEN best-practice scorecard shows Slough still scores on some national maintenance standards.
              That mixed picture is why a precise, evidence-led claim matters more than borough-wide headlines.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
            {' '}(published 2026, based on council transparency reports submitted by June 2025).
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            312.2km of roads — mostly residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Slough&apos;s June 2025 transparency report — where most pothole claims start
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">312.2km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">268.8km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">86% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">34.8km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">8.6km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <QuoteCard
            quote="Many users of the highway will have noticed that some of our roads have deteriorated in recent years following the financial difficulties the council has had."
            source="Slough Borough Council — Highways Maintenance Funding page"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the published condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Slough&apos;s June 2025 transparency report — with a 2022 data gap after
            Section 114
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>2022 caveat:</strong> Slough collected no A, B/C or U-road condition data in 2022. The council
              attributes this to the July 2021 Section 114 notice restricting spend to emergencies only, while a new
              survey framework contract was being tendered. Comparisons across 2022 are not possible.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (34.8km) — SCANNER surveyed annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono text-surface-400 italic">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold">65%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition rose from 2–3% pre-Section 114 to 7% in 2023 and 2024 — more than doubling on
              principal routes that make up 11% of the carriageway network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (8.6km) — 3% of the network</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono text-surface-400 italic">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                    <td className="px-4 py-2 text-center">49%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">26%</td>
                    <td className="px-4 py-2 text-center font-bold">66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads are a small share of the network, but RED condition rose from 3% in 2020 to 8% in 2023–2024,
              with amber roads peaking at 43% in 2023.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (268.8km) — CVI survey on a four-year cycle</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (published)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">16%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono text-surface-400 italic">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">No data collected</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">22%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium mb-3">
              Slough surveys 25% of U-roads annually via CVI (covering the full network over four years). RED-condition
              U-roads rose from 16% in 2020 to a peak of 25% in 2023, easing slightly to 22% in 2024 — roughly 59km of
              residential network in RED condition at the last survey.
            </p>
            <p className="text-amber-800 text-sm">
              The council notes CVI does not provide the same machine-measured precision as SCANNER, but is suited to
              lower-speed unclassified roads.
            </p>
          </div>

          <QuoteCard
            quote="S114 issued in July 21 – revenue and capital spend restricted to emergencies only"
            source="Slough Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">Following the money</h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            RED spend scorecard — capital budgets held flat since 2022/23 while pothole workload surged
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Slough highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-rose-900/20">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">1,888</td>
                  <td className="px-4 py-3 text-center font-bold">1,024</td>
                  <td className="px-4 py-3 text-center">864</td>
                  <td className="px-4 py-3 text-center text-emerald-400">65%</td>
                  <td className="px-4 py-3 text-center">35%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">1,129</td>
                  <td className="px-4 py-3 text-center">261</td>
                  <td className="px-4 py-3 text-center">868</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">1,129</td>
                  <td className="px-4 py-3 text-center">261</td>
                  <td className="px-4 py-3 text-center">868</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">1,129</td>
                  <td className="px-4 py-3 text-center">261</td>
                  <td className="px-4 py-3 text-center">868</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">979</td>
                  <td className="px-4 py-3 text-center">479</td>
                  <td className="px-4 py-3 text-center">500</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">878</td>
                  <td className="px-4 py-3 text-center">378</td>
                  <td className="px-4 py-3 text-center">500</td>
                  <td className="px-4 py-3 text-center">60%</td>
                  <td className="px-4 py-3 text-center">40%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is RED</h3>
              <p className="text-brand-200 text-sm">
                Total maintenance spend in 2024/25 was £1.129m (£261k capital plus £868k revenue). Capital spend has
                been frozen at £261k since 2022/23 even as DfT allocations rose. The council attributes early
                2020/21 disruption to council restructure and Covid-19 alongside Section 114 constraints.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Approximately 18% of 2024/25 reactive spend went directly to pothole intervention works. The
                preventative/reactive split has held at 60/40 since 2017 — yet estimated pothole fills averaged roughly
                958 per year over five years. Spend volume does not prove every defect was caught within inspection
                intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising pothole patch counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from Slough&apos;s June 2025 transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Slough estimated potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Context</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">377</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                  <td className="px-6 py-4 text-surface-600">Pre-Section 114; restructure and Covid impacts noted</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">408</td>
                  <td className="px-6 py-4 text-center text-amber-600">+8.2%</td>
                  <td className="px-6 py-4 text-surface-600">Section 114 issued July 2021</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,151</td>
                  <td className="px-6 py-4 text-center text-rose-600">+205%</td>
                  <td className="px-6 py-4 text-surface-600">Reactive backlog emerges</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,461</td>
                  <td className="px-6 py-4 text-center text-rose-600">+288%</td>
                  <td className="px-6 py-4 text-surface-600">Peak estimated fills</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,393</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+269%</td>
                  <td className="px-6 py-4 text-surface-600">Still well above pre-Section 114 levels</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">~1,200</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                  <td className="px-6 py-4 text-surface-600">May be lower if major schemes absorb defects</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Over the past five years, the estimated number of potholes repaired has varied from 377 (2020/21) to 1393 (2024/25), with an average of approximately 958 per year."
              source="Slough Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Reactive workload, not proof of prevention</h3>
              <p className="text-surface-600 text-sm">
                1,393 pothole repairs in 2024/25 works out to roughly 3.8 patches per day across 312.2km. That
                pattern shows safety-critical defects still forming faster than preventative resurfacing eliminates
                them — especially on U-roads measured on a four-year CVI cycle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 114 and recovery */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 114, recovery plans and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            What Slough&apos;s own June 2025 report says about financial constraints — and what that means for claims
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">July 2021 Section 114 notice</h3>
              <p className="text-surface-600 text-sm mb-3">
                Under the Local Government Finance Act 1988, a Section 114 notice signals that a council cannot balance
                its budget without exceptional measures. Slough&apos;s transparency report records that revenue and
                capital highway spend was then restricted to emergencies only — with no condition surveys in 2022.
              </p>
              <p className="text-surface-600 text-sm">
                That is the council&apos;s own explanation for disrupted maintenance — relevant context when assessing
                whether a reasonable inspection and repair system existed for your specific defect.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 recovery programme</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 5–10km carriageway resurfacing planned (subject to survey outcomes)</li>
                <li>• 2,439 m² carriageway patching projected</li>
                <li>• Major schemes: A4 cycleway, A4 safer roads, Destination Farnham Road, Stoke regeneration</li>
                <li>• Preventative share targeted at 65% (up from 60%)</li>
                <li>• Highway maintenance contract being re-tendered with warm-mix asphalt options</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Slough must maintain
              highways maintainable at public expense. To defend a claim under{' '}
              <strong className="text-white">Section 58</strong>, it must show a reasonable system for inspecting and
              repairing the specific defect — not just publish a mixed DfT scorecard or cite financial recovery plans.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road on the four-year CVI survey cycle — and had it been surveyed recently?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council form) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="It is the intention now for the service to treat the affected roads as efficiently as possible and to ensure that the public purse gets value for money."
            source="Slough Borough Council — Highways Maintenance Funding page"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a RED-rated borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Slough faces serious spend and condition challenges — but also documents recovery work
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN DfT best-practice scorecard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> APSE benchmarking membership cited in June 2025 report
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on A-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Major preventative schemes planned for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 65% preventative spend projected for 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect the council to point to recovery investment and risk-based asset management. Generic neglect
                arguments alone may not suffice.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall and spend DfT ratings — 1 of 13 RED authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 268.8km of U-roads — 86% of network — on a four-year CVI cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED condition at 22% in 2024 (peak 25% in 2023)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,393 pothole fills in 2024/25 — up 269% from 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council&apos;s own Section 114 &ldquo;emergencies only&rdquo; spending admission
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No condition data collected in 2022 during financial restrictions
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with RED spend ratings but GREEN best practice, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the four-year survey gap is your strongest structural argument</li>
              <li>• Section 114 context cited from the council&apos;s own transparency report where relevant</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Slough&apos;s
              own transparency data where it helps you — without overstating what the scorecards prove.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Slough Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack.
          </p>
          <a
            href={REPORT_POTHOLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a highways issue — slough.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Slough?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated borough with Section 114 history demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No Section 114 transparency citations</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road deterioration documented (22% RED in 2024)</li>
                <li>✅ Four-year CVI survey cycle argued</li>
                <li>✅ 1,393 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Slough</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=slough"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Slough Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href={TRANSPARENCY_REPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Slough Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>{' '}
            |{' '}
            <a
              href={HIGHWAYS_FUNDING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Slough Borough Council — Highways Maintenance Funding
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
